import { Location } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Subscription } from "rxjs";
import { filter } from "rxjs/internal/operators/filter";
import { appConfig } from "../../core-config";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { WebvoucherDialogComponent } from "../../modalDialogs/webvoucherDialog/webvoucherDialog.component";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { CoreCOMService } from "../../services/com.service";
import { CoreLogService } from "../../services/log.service";
import { SnackBarService, snackBarMessageKey } from "../../services/snackBar.service";
import { WebShopService } from "../../services/webShop.service";
import { WebVoucherService } from "../../services/web_voucher.service";
import { SnackBarMessageType } from "../../shared/contentTypes";
import { UserWebvoucherViewModel } from "../../shared/models/userInfo.model";
import { WebVoucherBalanceViewModel, WebVoucherNumberLength } from "../../shared/models/web_voucher.model";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { WebvoucherDetailService } from "./webvoucherDetail.service";
import Utils from "../../shared/utils";

@Component({
  selector: "webvoucherdetail",
  templateUrl: "./webvoucherDetail.component.html",
  styleUrls: ["./webvoucherDetail.component.scss"],
  providers: [WebvoucherDetailService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebvoucherDetailComponent implements OnInit, AfterViewInit, OnDestroy {

  public webvoucherBalanceViewModel: WebVoucherBalanceViewModel = new WebVoucherBalanceViewModel();
  public userWebvoucherViewModel: UserWebvoucherViewModel = new UserWebvoucherViewModel();
  public isOnline: boolean;
  public isSmallView: boolean = false;

  private langSubscriber: Subscription;

  // Emits `true` when the QR code is visible; used to trigger brightness setup responsively.
  public qrCodeVisible$ = new BehaviorSubject(false);
  public webVoucherNo: string = "";

  constructor(private _location: Location
    , private _snackBarService: SnackBarService
    , private _coreLogService: CoreLogService
    , private _coreCOMService: CoreCOMService
    , private _changeDetectorRef: ChangeDetectorRef
    , private _webvoucherDetailService: WebvoucherDetailService
    , private _overlaySpinnerService: OverlaySpinnerService
    , private _router: Router
    , private _webvoucherService: WebVoucherService
    , private _internationalisationService: InternationalisationService
    , private _webShopService: WebShopService
    , private _bttomSheetHelperService: BottomSheetHelperService
    , _mainnavService: MainnavService) {
    this.isSmallView = _mainnavService.viewType === ViewType.Small;
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("WebvoucherDetailComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    me.isOnline = me._coreCOMService.environment.networkInformation.isOnline;
    me.langSubscriber = me._internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    me.getWebvoucherDetailViewModel();
  }

  async ngAfterViewInit(): Promise<void> { }

  private getWebvoucherDetailViewModel() {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    Promise.all([me._webvoucherDetailService.GetWebvoucherQRCode()
      , me._webvoucherService.GetWebVoucherBalanceViewModel()])
      .then((result) => {
        me.userWebvoucherViewModel = result[0];
        const storageBalance = result[1];
        if (storageBalance !== null) {
          me.webvoucherBalanceViewModel = storageBalance;
        }
        me.webVoucherNo = me.userWebvoucherViewModel.webvoucherNumber.trim();
        if (me.webVoucherNo.length === WebVoucherNumberLength) {
          // if first load, than with loader, on refrsh without loader
          // wenn ein Voucher hinterlegt ist, dann die Balance nachladen
          me.refreshWebvoucher(storageBalance === null)
          me.qrCodeVisible$.next(true);
        }
        me.PushChanges();
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch(e => {
        me._coreLogService.error("WebvoucherDetailComponent.getWebvoucherDetailViewModel", "Promise.all", e);
        me._overlaySpinnerService.HideLayoutSpinner();
        me._snackBarService.ShowErrorMessageFailed(106);
      });
  }

  public addToWallet(walletReferenceId: string, type: "Voucher" | "Ticket", walletType: "Google" | "Apple") {
    const me = this;
    console.log("WalletReferenceId", me.webvoucherBalanceViewModel.walletReferenceId);
    if (Utils.IsNullOrEmpty(walletReferenceId)) {
      me._coreLogService.error("WebvoucherDetailComponent.addToWallet", "walletReferenceId", "missing");
      me._snackBarService.ShowErrorMessageFailed(132);
      return;
    }
    me._webvoucherService.addToWallet(walletReferenceId, type, walletType)
      .then(() => {
        me._snackBarService.ShowMessage(snackBarMessageKey.copiedToClipboard, SnackBarMessageType.Successful);
      })
      .catch((e: any) => {
        me._coreLogService.error("WebvoucherDetailComponent.addToWallet", "addToWallet (catch)", e);
        me._snackBarService.ShowErrorMessageFailed(132);
      });
  }

  public OpenWebVoucherModal() {
    const me = this;
    const webvoucherModal = me._bttomSheetHelperService.open(WebvoucherDialogComponent, {
      data: me.userWebvoucherViewModel.webvoucherNumber,
      autoFocus: false
    });
    webvoucherModal.afterDismissed().subscribe((promise: Promise<boolean>) => {
      if (promise) {
        promise
          .then((response) => {
            if (response) {
              me._snackBarService.ShowMessage(snackBarMessageKey.successfullyExecuted, SnackBarMessageType.Successful);
              me._router.navigateByUrl("/webvoucher");
            } else {
              me._snackBarService.ShowMessage(snackBarMessageKey.failed, SnackBarMessageType.Successful);
            }
          })
          .catch((e) => {
            this._coreLogService.error("WebvoucherDetailComponent.openWebVoucherModal", "webvoucherModal.afterClosed (catch)", e);
            me._snackBarService.ShowErrorMessageFailed(107);
          });
      } else {
        // the modal dialog has been closed; Do nothing.
      }
    });
  }

  // format price base on language
  public priceFormatter(price: number, hasSymbol: boolean = true): string {
    const me = this;
    return me._webShopService.formatCurrency(price, hasSymbol);
  }

  public refreshWebvoucher(with_loader: boolean) {
    const me = this;
    if (with_loader) {
      me._overlaySpinnerService.ShowLayoutSpinner();
    }
    me._webvoucherDetailService.GetWebvoucherBalanceViewModel().then((balance_result) => {
      me.webvoucherBalanceViewModel = balance_result;
      me.PushChanges();
      if (with_loader) {
        me._overlaySpinnerService.HideLayoutSpinner();
      }
    })
      .catch(e => {
        me._coreLogService.error("WebvoucherDetailComponent.refreshWebvoucher", "GetWebvoucherBalanceViewModel", e);
        me._snackBarService.ShowErrorMessageFailed(108);
        if (with_loader) {
          me._overlaySpinnerService.HideLayoutSpinner();
        }
      });
  }

  /**When small view is true, navigation back by swiping from left to right.*/
  public SwiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me.navigateBack();
    }
  }

  public navigateBack(): void {
    const me = this;
    me._location.back();
  }

  public async ngOnDestroy(): Promise<void> {
    const me = this;
    if (me.langSubscriber) {
      me.langSubscriber.unsubscribe();
    }
  }
}
